import {
  type TAddress,
  formatAddress,
} from '@modules/_shared/utils/formatting';

// The only address an MLS listing may show an agent: `effective_address`,
// a computed field gated by `mls_lot_effective_address` (see that
// migration for the rule). Never mask again on this side, and never read
// `property.route / street_number / locality / postcode / lat / lng` for
// an MLS lot — role `agent` is not granted those columns.
//
// The field is `jsonb`, so it arrives untyped and is parsed here once.
export type EffectiveAddress = {
  route: string | null;
  street_number: string | null;
  locality: string | null;
  postcode: string | null;
  state: string | null;
  country_code: string | null;
  lat: number | null;
  lng: number | null;
  // lat / lng is the ~1 km cell, not a point: draw no pin on it.
  approximate: boolean;
};

const EMPTY_EFFECTIVE_ADDRESS: EffectiveAddress = {
  route: null,
  street_number: null,
  locality: null,
  postcode: null,
  state: null,
  country_code: null,
  lat: null,
  lng: null,
  approximate: false,
};

// A lot whose `property` has had the gated address projected onto it.
// The generated types no longer carry route / street_number / locality /
// postcode / state / country_code / lat / lng on mls_properties (role
// `agent` is not granted them), so the projection is what puts them back,
// and this is the type that says so.
export type WithEffectiveAddress<T extends { property?: unknown }> = Omit<
  T,
  'property'
> & {
  property: (NonNullable<T['property']> & EffectiveAddress) | null;
};

// formatAddress's short format drops the state, and the state is all a
// restricted lot on the default option discloses: fall back to it rather than
// render a blank label.
export const mlsAddressLines = (address: TAddress): string[] => {
  const lines = formatAddress(address, null);
  return lines.length > 0 || address.state == null ? lines : [address.state];
};

export const mlsAddressLabel = (address: TAddress): string | null =>
  mlsAddressLines(address).join(', ') || null;

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

export const readEffectiveAddress = (raw: unknown): EffectiveAddress => {
  if (raw == null || typeof raw !== 'object') {
    return EMPTY_EFFECTIVE_ADDRESS;
  }
  const obj = raw as Record<string, unknown>;
  return {
    route: str(obj.route),
    street_number: str(obj.street_number),
    locality: str(obj.locality),
    postcode: str(obj.postcode),
    state: str(obj.state),
    country_code: str(obj.country_code),
    lat: num(obj.lat),
    lng: num(obj.lng),
    approximate: obj.approximate === true,
  };
};
